import * as React from 'react';
import {
  useDesignerSelector,
  type DesignerStores,
} from '@packages/systems/designer/flux';
import type {ExpressionElement} from '@packages/systems/wfdl-ast';
import {emptyArray} from '@packages/utilities/fp/utils';
import type {
  InstalledApp,
  AppLaunchLocation,
  CurrentAppIntent,
} from '@packages/systems/designer/apps/types';
import {
  getInstalledAppsSelector,
  getAppConnectionsFromExpressionElement,
  getAppConnectionElement,
} from '@packages/systems/designer/apps/utils';
import {Expressions, Plugins} from '@packages/systems/wfdl';
import type {CurrentAppConnection} from '@packages/systems/core/records/DesignerState';
import {
  getName,
  getPluginName,
  pluginName,
} from '@packages/systems/wfdl-Qualified';
import {selectDesignerAbility} from '@packages/systems/designer-abilities';

export type AppConnectionItem = {
  app: InstalledApp;
  currentAppConnection: CurrentAppConnection;
  location: AppLaunchLocation;
  currentAppIntent?: CurrentAppIntent;
};

const sitePluginName = pluginName('__SitePlugin');
const selectCurrentPageId = (state: DesignerStores) =>
  state.DesignerStore.currentPageId;

// arbitrary limit of App Connections to display in the UI.
// we can increase this in the future if needed, we'll just want to test out the UI to see how it looks.
const DISPLAY_LIMIT = 5;

export const useAppConnections = (
  initialElement: ExpressionElement,
  componentType: Plugins.QualifiedElement,
  location: AppLaunchLocation
): Array<AppConnectionItem> => {
  const canManageAppConnections = useDesignerSelector(
    selectDesignerAbility('canAccessApps')
  );
  const installedApps = useDesignerSelector(getInstalledAppsSelector);
  const currentPageId = useDesignerSelector(selectCurrentPageId);

  return React.useMemo(() => {
    if (!canManageAppConnections || !installedApps?.length || !initialElement) {
      return emptyArray;
    }

    const element = getAppConnectionElement(initialElement);
    const connections = getAppConnectionsFromExpressionElement(element);

    if (!connections.length) return emptyArray;

    const plugin = getPluginName(componentType);
    if (plugin !== sitePluginName) return emptyArray;
    let componentId = getName(componentType);

    // The Designer works with one page at a time and identifies each page as just
    // "page", but we need to be able to discriminate between elements of
    // different pages since extensions can reference elements from multiple pages
    // at the same time.
    if (componentId === 'page') {
      componentId = currentPageId;
    }

    const resp = connections.flatMap((connection) => {
      const app = installedApps.find((a) => a.id === connection.id);
      if (!app) return emptyArray;
      return [
        {
          app,
          location,
          currentAppConnection: {
            value: connection.data.value,
            resourceId: Expressions.getElementId(element),
            resourceParentId: componentId,
            resourceType: 'element',
          },
        },
      ];
    });

    return resp.slice(0, DISPLAY_LIMIT);
  }, [
    initialElement,
    installedApps,
    canManageAppConnections,
    componentType,
    currentPageId,
    location,
  ]);
};
